import {
  Activity,
  HeartPulse,
  Bone,
  Sparkles,
  ShieldPlus,
  Footprints,
  Leaf,
  Flower2,
  Users,
  ArrowRight,
} from "lucide-react";
import { Sparkle, JointDrawing, DottedArc } from "./Decorations";

const CARDS = [
  {
    icon: Activity,
    title: "Arthritis",
    desc:
      "Inflammatory and degenerative joint conditions, including rheumatoid arthritis, psoriatic arthritis, and osteoarthritis.",
  },
  {
    icon: ShieldPlus,
    title: "Lupus",
    desc:
      "A complex autoimmune condition that affects many organ systems, early diagnosis and ongoing care matter.",
  },
  {
    icon: Bone,
    title: "Osteoporosis",
    desc:
      "Bone density loss and fracture prevention through nutrition, movement, screening, and modern therapies.",
  },
  {
    icon: HeartPulse,
    title: "Joint Pain",
    desc:
      "When everyday aches signal something more, and how to find lasting relief through targeted care.",
  },
  {
    icon: Sparkles,
    title: "Autoimmune Disease",
    desc:
      "Understanding how the immune system can misfire and the conditions that follow, from Sjögren's to scleroderma.",
  },
  {
    icon: Footprints,
    title: "Healthy Movement",
    desc:
      "Low-impact, joint-friendly exercise that builds strength, flexibility, and confidence at every age.",
  },
  {
    icon: Leaf,
    title: "Lifestyle & Wellness",
    desc:
      "Nutrition, sleep, stress, and daily habits that support whole-body health alongside medical care.",
  },
  {
    icon: Flower2,
    title: "Women's Health",
    desc:
      "Rheumatologic care across the female lifespan, pregnancy, perimenopause, bone health, and beyond.",
  },
  {
    icon: Users,
    title: "Minority Health",
    desc:
      "Closing the gap on autoimmune awareness and access to specialty care in underserved communities.",
  },
];

export default function JointHealth() {
  return (
    <section
      id="joint-health"
      data-testid="joint-section"
      className="relative py-28 bg-white overflow-hidden"
    >
      <JointDrawing className="absolute -top-2 right-4 opacity-25 hidden lg:block" size={140} />
      <Sparkle className="absolute top-32 left-6" color="#8E44AD" size={20} />
      <DottedArc className="absolute bottom-12 left-1/3 w-72 opacity-50" color="#C7A5D9" />

      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="max-w-3xl">
          <p className="pill mb-5 reveal">Education</p>
          <h2
            className="h-display text-3xl sm:text-4xl title-underline reveal"
            data-testid="joint-title"
          >
            Joint Health &amp; Autoimmune Education
          </h2>
          <p className="mt-7 text-lg text-charcoal/80 reveal">
            <span className="signature text-3xl text-royal align-middle mr-1">"</span>
            I'll help you handle the joint stuff.
            <span className="signature text-3xl text-royal align-middle ml-1">"</span>
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {CARDS.map((c, i) => (
            <article
              key={c.title}
              data-testid={`joint-card-${i}`}
              className={`card-soft p-7 ${i % 3 === 0 ? "reveal-left" : i % 3 === 1 ? "reveal-scale" : "reveal-right"} group glow-accent`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="h-12 w-12 rounded-xl bg-lavender-bg text-royal grid place-items-center group-hover:bg-royal group-hover:text-white group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                <c.icon size={22} strokeWidth={1.7} />
              </div>
              <h3 className="mt-5 font-display text-[15px] tracking-[0.18em] uppercase text-plum">
                {c.title}
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-charcoal/75">{c.desc}</p>
              <a
                href="#blog"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("blog")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="mt-5 inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-royal hover:text-plum transition-colors"
              >
                Learn More <ArrowRight size={14} />
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
